import { useState, useEffect } from 'react'
import { supabaseGetter } from '~/api/supabase-getter'
import { useAuthContext } from './context/auth/auth-context'

const REFRESH_EVENT = 'currentUserRefresh'

/** Call this after mutating user features/preferences to force all useCurrentUser and useFeatures instances to re-fetch. */
export const refreshCurrentUser = () => {
  supabaseGetter.users.invalidate()
  supabaseGetter.features.invalidate()
  window.dispatchEvent(new CustomEvent(REFRESH_EVENT))
}

/**
 * Custom hook for fetching current user data from Supabase
 * Uses the caching layer for improved performance
 *
 * @returns {Object} { currentUser, userPreferences, loading, error, invalidate }
 */
export const useCurrentUser = () => {
  const [currentUser, setCurrentUser] = useState()
  const [userPreferences, setUserPreferences] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [refreshCount, setRefreshCount] = useState(0)
  const { user: authUser } = useAuthContext()

  useEffect(() => {
    const handleRefresh = () => setRefreshCount(c => c + 1)
    window.addEventListener(REFRESH_EVENT, handleRefresh)
    return () => window.removeEventListener(REFRESH_EVENT, handleRefresh)
  }, [])

  useEffect(() => {
    let isMounted = true

    function extractJSON(blob) {
      return Object.values(blob).reduce((acc, entry) => {
        const [key, value] = Object.entries(entry)[0]
        acc[key] = value
        return acc
      }, {})
    }

    async function fetchCurrentUser() {
      try {
        if (isMounted) {
          setLoading(true)
          setError(null)
        }

        if (!authUser) {
          return
        }

        const { data, error: fetchError } = await supabaseGetter.users.getById(authUser.id)

        if (!isMounted) return

        if (fetchError) {
          console.warn('Error fetching current user:', fetchError)
          setError(fetchError.message || 'Failed to fetch current user')
          setCurrentUser()
        } else if (data) {
          setCurrentUser(data)
          const preferences = extractJSON(data?.preferences || {})
          setUserPreferences(preferences)
        }
      } catch (err) {
        if (!isMounted) return

        console.warn('Error in current user:', err)
        setError('An unexpected error occurred')
        setCurrentUser()
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchCurrentUser()

    return () => {
      isMounted = false
    }
  }, [authUser, refreshCount])

  return {
    currentUser,
    userPreferences,
    loading,
    error,
    invalidate: supabaseGetter.users.invalidate
  }
}
